import { useEffect, useRef, useState } from "react";
import { renderToString } from "react-dom/server";
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AdCreative, PublishAdCreative } from "@/screens/creative-library/types";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  Table as ReactTable,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Button } from "@/components/ui/button";
import { Check, Edit } from "lucide-react";
import { RxSize } from "react-icons/rx";
import GalleryView from "../../../GalleryView";
// (removed duplicate react import)
import {
  approvePublishAdCreative,
  cancelPublishAdCreative,
  fetchMedia,
} from "@/screens/creative-library/apiService";
import { useParams } from "react-router-dom";
import LoadingSpinner from "@/components/common/loaders/LoadingSpinner";
import PublishAction from "../../../adActions/PublishAction";
import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import { LuCircleCheck, LuTriangleAlert } from "react-icons/lu";
import emptyStateSvg from "@/assets/creativeLibraryAssets/publishTableEmptyState.svg";
import { useCreativeLibraryStore } from "@/screens/creative-library/store";
import AdCard from "@/components/common/adCard";
import DownloadAction from "@/components/common/adCard/actions/DownloadAction";
import { IconCards } from "@tabler/icons-react";
import EditLogo from "../actions/EditLogo";
import EditLogoDialog from "../dialog/EditLogoDialog";
import { useGlobalStore } from "@/GlobalStore";
import { Image as ImageType } from "@/screens/creatives/types";
import WaveLoader from "@/components/common/loaders/WaveLoader";
import { getMediaIcon } from "@/screens/utils/Helpers";

interface Props {
  data: UnpublishedTableData[];
  pollingCompleteAdCreativesCb: (
    adCreatives: AdCreative[],
    publishCreativeId: string,
  ) => void;
  updatePublishAdCreative: (updatedPublishedAdCreative: PublishAdCreative) => void;
  onCancel?: (canceledPublishAdCreativeId: string) => void;
  onApprove?: (approvedPublishAdCreative: PublishAdCreative) => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

export type UnpublishedTableData = {
  ad: PublishAdCreative;
  publishingAsset: PublishingAsset;
  primaryText: string[];
};

export type PublishingAsset = {
  campaign: {
    id: string;
    name: string;
  };
  adset: {
    id: string;
    name: string;
  };
};

const RenderPublishAdCreative = ({
  publishAdCreative,
  accountId,
  setSelectedPublishAdCreative,
}: {
  publishAdCreative: PublishAdCreative;
  accountId: string | undefined;
  pollingCompleteCb: (adCreatives: AdCreative[]) => void;
  setSelectedPublishAdCreative: (publishAdCreative: PublishAdCreative) => void;
}) => {
  const { setOpenGallery, setGalleryAdCreatives } = useCreativeLibraryStore();
  const baseCreative = publishAdCreative.ad_creative;
  const imageUrl =
    publishAdCreative?.image_url ||
    publishAdCreative?.ad_creative?.creative?.images[0]?.permalink_url ||
    publishAdCreative?.ad_creative?.creative?.videos[0]?.thumbnail_url;
  const creativeType = publishAdCreative?.ad_creative?.creative_type;
  // Poll using individual media (image) ids
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const inFlightRef = useRef<Set<string>>(new Set());
  const isMountedRef = useRef<boolean>(true);
  const [pendingIds, setPendingIds] = useState<string[]>([]);
  const [isPolling, setIsPolling] = useState<boolean>(false);

  const schedulePoll = (mediaId: string) => {
    const existing = timeoutsRef.current.get(mediaId);
    if (existing) clearTimeout(existing);
    const t = setTimeout(() => void pollOnce(mediaId), 5000);
    timeoutsRef.current.set(mediaId, t);
  };

  const pollOnce = async (mediaId: string) => {
    if (!accountId || inFlightRef.current.has(mediaId) || !isMountedRef.current) return;
    try {
      inFlightRef.current.add(mediaId);
      const res = await fetchMedia(accountId, mediaId);
      const status = res?.data?.status;
      if (!isMountedRef.current) return;
      if (status === "pending") {
        schedulePoll(mediaId);
        setIsPolling(true);
      } else {
        const t = timeoutsRef.current.get(mediaId);
        if (t) clearTimeout(t);
        timeoutsRef.current.delete(mediaId);
        setPendingIds((prev) => prev.filter((id) => id !== mediaId));
      }
    } catch {
      schedulePoll(mediaId);
    } finally {
      inFlightRef.current.delete(mediaId);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    // Capture ref values for cleanup
    const timeouts = timeoutsRef.current;
    const inFlight = inFlightRef.current;

    const images = baseCreative?.creative.images || [];
    const initialPending = images
      .filter((im) => im.status === "pending")
      .map((im) => String(im.id));
    setPendingIds(initialPending);
    setIsPolling(initialPending.length > 0);
    initialPending.forEach((id) => void pollOnce(id));
    return () => {
      isMountedRef.current = false;
      timeouts.forEach((t) => clearTimeout(t));
      timeouts.clear();
      inFlight.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, baseCreative?.id, baseCreative?.creative.images?.length]);

  // Reflect current polling state from pending media ids
  useEffect(() => {
    setIsPolling(pendingIds.length > 0);
  }, [pendingIds.length]);

  const handleOpenGallery = () => {
    setOpenGallery(true);
    // Prefer new shape: single ad_creative; fallback to legacy array
    if (baseCreative) {
      const images = baseCreative.creative?.images || [];
      const multiImageItems: AdCreative[] = images.length
        ? images.map((img) => ({
            ...baseCreative,
            id: `${baseCreative.id}-${img.id}`,
            creative: {
              ...baseCreative.creative,
              images: [img],
              configuration: img.metadata?.configuration,
            },
          }))
        : [baseCreative];
      setGalleryAdCreatives(multiImageItems);
    } else if (Array.isArray(publishAdCreative.ad_creatives)) {
      setGalleryAdCreatives(publishAdCreative.ad_creatives);
    } else {
      setGalleryAdCreatives([]);
    }
    setSelectedPublishAdCreative(publishAdCreative);
  };

  return (
    <div className="flex gap-2 items-center min-w-[300px] py-2">
      <div className="w-[67px] h-[67px] rounded-[8px] overflow-hidden">
        <img className="w-full h-full object-cover" src={imageUrl} alt="" />
      </div>
      <div className="flex flex-col gap-2">
        <span className="font-inter font-normal text-[14px] leading-[20px] tracking-[0%] text-brand-primary-new truncate max-w-[184px] overflow-hidden text-ellipsis">
          {baseCreative?.name || ""}
        </span>
        <div className="flex gap-2 items-center">
          {getMediaIcon(creativeType, true, 16, 32)}
          <Button
            variant={"outline"}
            className="flex items-center gap-2"
            onClick={handleOpenGallery}
            disabled={isPolling}
          >
            {!isPolling ? (
              <IconCards size={16} color="#021859CC" />
            ) : (
              <LoadingSpinner
                size={16}
                color="#FF4500"
                className="opacity-100 animate-spin"
              />
            )}
            <span className="text-brand-primary-800 font-normal text-[12px] leading-[19.16px] tracking-[2%] relative top-[1px]">
              Show Ad
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ className }: { className?: string }) => (
  <div className={`h-full flex flex-col items-center justify-center py-10 ${className}`}>
    <img src={emptyStateSvg} alt="No ads published" />
    <div className="font-medium text-[18px] leading-[100%] tracking-[0%] text-center text-brand-primary-new mt-6">
      No ads to publish yet
    </div>
    <p className="font-normal text-[16px] leading-[100%] tracking-[0%] text-center text-[#617390] mt-3">
      Publish an ad and it will appear here
    </p>
  </div>
);

// Virtualized table body component
interface VirtualizedTableBodyProps {
  table: ReactTable<UnpublishedTableData>;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  enabled?: boolean;
}

function VirtualizedTableBody({
  table,
  tableContainerRef,
  isLoadingMore,
  hasMore,
  onLoadMore,
  enabled = true,
}: VirtualizedTableBodyProps) {
  const { rows } = table.getRowModel();
  const loadMoreTriggeredForCountRef = useRef<number | null>(null);

  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: rows.length,
    estimateSize: () => 100, // estimate row height
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== "undefined" && navigator.userAgent.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
    enabled,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  // Reset the trigger ref when new data is loaded
  useEffect(() => {
    if (!isLoadingMore && loadMoreTriggeredForCountRef.current !== null) {
      // Data has been loaded, allow triggering again for new scroll positions
      if (rows.length > loadMoreTriggeredForCountRef.current) {
        loadMoreTriggeredForCountRef.current = null;
      }
    }
  }, [isLoadingMore, rows.length]);

  // Check for pagination when virtualizer scrolls near the end
  useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1];
    if (!lastItem) return;

    const shouldLoadMore =
      lastItem.index >= rows.length - 3 &&
      hasMore &&
      !isLoadingMore &&
      onLoadMore &&
      loadMoreTriggeredForCountRef.current !== rows.length;

    if (shouldLoadMore) {
      loadMoreTriggeredForCountRef.current = rows.length;
      onLoadMore();
    }
  }, [virtualItems, rows.length, hasMore, isLoadingMore, onLoadMore]);

  return (
    <tbody
      style={{
        display: "grid",
        height: `${totalSize}px`,
        position: "relative",
      }}
    >
      {virtualItems.map((virtualRow) => {
        const row = rows[virtualRow.index];
        return (
          <TableRow
            key={row.id}
            data-index={virtualRow.index}
            ref={(node) => rowVirtualizer.measureElement(node)}
            className=" hover:bg-white flex w-full"
            style={{
              position: "absolute",
              transform: `translateY(${virtualRow.start}px)`,
            }}
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell
                key={cell.id}
                className="border-r last:border-r-0 border-border-gray flex-1 flex items-center"
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        );
      })}
    </tbody>
  );
}

const UnpublishedTable = (props: Props) => {
  const {
    data,
    pollingCompleteAdCreativesCb,
    updatePublishAdCreative,
    onApprove,
    onCancel,
    hasMore,
    isLoadingMore,
    onLoadMore,
  } = props;
  const [sorting, setSorting] = useState<SortingState>([]);
  const [approvingIds, setApprovingIds] = useState<Record<string, boolean>>({});
  const [cancelingIds, setCancelingIds] = useState<Record<string, boolean>>({});
  const [editLogoDialogOpen, setEditLogoDialogOpen] = useState(false);
  const [selectedPublishAdCreative, setSelectedPublishAdCreative] =
    useState<PublishAdCreative | null>(null);
  const [selectedAdForLogoEdit, setSelectedAdForLogoEdit] = useState<AdCreative | null>(
    null,
  );
  const { accountId } = useParams();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [isContainerReady, setIsContainerReady] = useState(false);

  // Track when container is mounted to trigger virtualizer recalculation
  useEffect(() => {
    if (tableContainerRef.current) {
      setIsContainerReady(true);
    }
    return () => setIsContainerReady(false);
  }, []);

  const handleApprove = async (publishCreativeId: string) => {
    if (!accountId) return;

    try {
      setApprovingIds((prev) => ({ ...prev, [publishCreativeId]: true }));
      const res = await approvePublishAdCreative(accountId, publishCreativeId);
      if (res.status === "failed") {
        toast.message(
          <ToastContent
            icon={LuTriangleAlert}
            headingPrefix="Failed to Publish Ad"
            subheadingSuffix={
              res?.message ?? "We couldn't publish your ad. Please try again."
            }
            isError={true}
          />,
        );
        return;
      }
      if (onApprove && res.data) {
        onApprove(res.data);
      }
      toast.message(
        <ToastContent
          icon={LuCircleCheck}
          headingPrefix="Ad Published Successfully"
          subheadingSuffix="Switch to the Published tab to view your live ad."
          isError={false}
        />,
      );
    } catch (error) {
      console.log(error);
    } finally {
      setApprovingIds((prev) => ({ ...prev, [publishCreativeId]: false }));
    }
  };

  const handleCancel = async (publishCreativeId: string) => {
    if (!accountId) return;
    try {
      setCancelingIds((prev) => ({ ...prev, [publishCreativeId]: true }));
      await cancelPublishAdCreative(accountId, publishCreativeId);
      if (onCancel) {
        onCancel(publishCreativeId);
      }
      toast.message(
        <ToastContent
          icon={LuCircleCheck}
          headingPrefix="Success"
          subheadingSuffix="Ad creative canceled successfully."
          isError={false}
        />,
      );
    } catch (error) {
      console.error("Error canceling ad creative:", error);
    } finally {
      setCancelingIds((prev) => ({ ...prev, [publishCreativeId]: false }));
    }
  };

  const columns: ColumnDef<UnpublishedTableData>[] = [
    {
      accessorKey: "ad",
      header: "Ad",
      cell: ({ row }) => (
        <RenderPublishAdCreative
          publishAdCreative={row.original.ad}
          accountId={accountId}
          pollingCompleteCb={(adCreatives) => {
            pollingCompleteAdCreativesCb(adCreatives, row.original.ad.id);
          }}
          setSelectedPublishAdCreative={(publishAdCreative) => {
            setSelectedPublishAdCreative(publishAdCreative);
          }}
        />
      ),
    },
    {
      accessorKey: "publishingAsset",
      header: "Campaign",
      cell: ({ row }) => (
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col max-w-[280px]">
            <div>
              <span className="font-medium text-[12.6px] leading-[18px] tracking-[0%] text-brand-primary-new">
                Campaign:{" "}
              </span>
              <span className="font-normal text-[12.6px] leading-[18px] tracking-[0%] text-brand-primary-new break-all whitespace-normal">
                {row.original.publishingAsset.campaign.name}
              </span>
            </div>
            <div>
              <span className="font-medium text-[12.6px] leading-[18px] tracking-[0%] text-brand-primary-new">
                Adset:{" "}
              </span>
              <span className="font-normal text-[12.6px] leading-[18px] tracking-[0%] text-brand-primary-new break-all whitespace-normal">
                {row.original.publishingAsset.adset.name}
              </span>
            </div>
          </div>
          <PublishAction
            creativeId={row.original.ad.ad_creative.id}
            published={false}
            update
            onUpdate={updatePublishAdCreative}
            trigger={({ isPublishing }) => {
              return (
                <Button
                  variant={"ghost"}
                  className="hover:bg-[#F6F6F6] h-9 w-9 disabled:bg-white"
                  disabled={isPublishing}
                >
                  {isPublishing ? (
                    <LoadingSpinner
                      size={16}
                      className="animate-spin shrink-0 text-[#FF4500]"
                    />
                  ) : (
                    <Edit
                      color="#021859CC"
                      size={16}
                      className="shrink-0 !text-brand-primary-800"
                    />
                  )}
                </Button>
              );
            }}
          />
        </div>
      ),
    },
    {
      accessorKey: "primaryText",
      header: "Primary Text",
      cell: ({ row }) => (
        <div className="min-w-[160px] max-w-[280px] font-['Inter'] text-[12.6px] leading-[18px] tracking-[0%] text-brand-primary-new break-words whitespace-normal font-normal">
          {row.original.primaryText.join(" • ")}
        </div>
      ),
    },
    {
      accessorKey: "",
      header: "Options",
      cell: ({ row }) => {
        const isApproving = approvingIds[row.original.ad.id] || false;
        const isCanceling = cancelingIds[row.original.ad.id] || false;
        const isPending = Array.isArray(row.original.ad.ad_creative.creative.images)
          ? row.original.ad.ad_creative.creative.images.some(
              (im) => im.status === "pending",
            )
          : false;

        return (
          <div className="flex gap-2 font-normal text-[12.6px] leading-[18px] tracking-[0%] text-brand-primary-new w-[190px] border-0">
            {!isApproving ? (
              <>
                <Button
                  variant={"default"}
                  className="bg-[#FF4500] gap-1 hover:bg-[#FF4500CC] text-white border-0"
                  disabled={isPending || isCanceling}
                  onClick={() => void handleApprove(row.original.ad.id)}
                >
                  <Check size={14} className="shrink-0" />
                  <span className="font-normal text-[12px] leading-[19.16px] tracking-[2%]">
                    Approve
                  </span>
                </Button>

                <Button
                  variant={"outline"}
                  className="font-normal text-[12px] leading-[19.16px] tracking-[2%] min-w-[90px] flex items-center justify-center gap-1"
                  disabled={isCanceling}
                  onClick={() => void handleCancel(row.original.ad.id)}
                >
                  {isCanceling ? (
                    <LoadingSpinner
                      size={14}
                      className="animate-spin shrink-0 text-brand-primary-new"
                    />
                  ) : null}
                  <span>Cancel</span>
                </Button>
              </>
            ) : (
              <>
                <Button variant={"outline"} className="shadow-sm gap-2 hover:bg-white">
                  <LoadingSpinner
                    size={14}
                    className="animate-spin shrink-0 !text-[#FF4500]"
                  />
                  <span className="text-[#35467A] font-normal text-[12px] leading-[19.16px] tracking-[2%]">
                    Publishing...
                  </span>
                </Button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const hasRows = table.getRowModel().rows?.length > 0;
  const { galleryAdCreatives, openGallery, setOpenGallery, setGalleryAdCreatives } =
    useCreativeLibraryStore();
  const { selectedAccount } = useGlobalStore();

  if (!hasRows) {
    return (
      <EmptyState className="border-x-[1px] border-b-[1px] border-solid border-border-gray rounded-b-[12px]" />
    );
  }

  return (
    <>
      <div
        ref={tableContainerRef}
        className="scrollable-table-container overflow-auto max-h-[calc(100vh-204px)] border-x-[1px] border-b-[1px] border-solid border-border-gray rounded-b-[12px]"
        style={{ position: "relative" }}
      >
        <Table
          className="border-separate border-spacing-0 bg-white"
          style={{ display: "grid" }}
        >
          <TableHeader
            className="bg-white z-10"
            style={{ display: "grid", position: "sticky", top: 0 }}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-white flex w-full">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="border-b border-r border-border-gray last:border-r-0 flex-1 flex items-center"
                  >
                    {header.isPlaceholder ? null : (
                      <div className="font-['Inter'] font-medium text-[12.6px] leading-[18px] tracking-[0%] text-[#71717A]">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <VirtualizedTableBody
            table={table}
            tableContainerRef={tableContainerRef}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            enabled={isContainerReady}
          />
        </Table>
        {isLoadingMore && (
          <div className="h-[50px] overflow-hidden flex items-center justify-center bg-white">
            <WaveLoader className="w-[100px]" />
          </div>
        )}
      </div>
      <GalleryView
        open={openGallery}
        onOpenChange={(open) => {
          if (!open) {
            setOpenGallery(false);
            setGalleryAdCreatives([]);
          }
        }}
        items={galleryAdCreatives}
        renderItem={(ad) => (
          <AdCard
            header={{
              icon:
                ad?.creative?.account_icon_url ||
                selectedAccount?.additional_info?.ad_account_icon_url,
              title: ad?.creative?.account_name ?? "",
            }}
            body={{
              icon_src: `data:image/svg+xml;base64,${btoa(renderToString(<RxSize size={18} color="#021859CC" />))}`,
              name: ad.creative?.images?.[0]?.aspect_ratio ?? "",
              description: ad?.creative.bodies?.[0],
              media: {
                ad_id: ad.id || "",
                source: ad.creative?.videos?.[0]
                  ? ad.creative?.videos?.[0]?.permalink_url
                  : ad.creative?.images?.[0]?.permalink_url,
                type: ad.creative?.videos?.[0] ? "video" : "image",
                thumbnail: ad.creative.thumbnail_url,
                actions: [
                  {
                    component: (
                      <DownloadAction
                        url={
                          ad.creative_type === "video"
                            ? ad.creative?.videos?.[0]?.permalink_url
                            : ad.creative?.images?.[0]?.uncompressed_url ||
                              ad.creative?.images?.[0]?.permalink_url
                        }
                        type={ad.creative_type}
                        aspectRatio={
                          ad.creative_type === "video"
                            ? ad.creative?.videos?.[0]?.aspect_ratio
                            : ad.creative?.images?.[0]?.aspect_ratio
                        }
                      />
                    ),
                    position: "top-right",
                    visibility: "hover",
                  },
                  ...(ad.creative.configuration
                    ? [
                        {
                          component: (
                            <EditLogo
                              onClick={() => {
                                setSelectedAdForLogoEdit(ad);
                                setEditLogoDialogOpen(true);
                                setOpenGallery(false);
                              }}
                            />
                          ),
                          position: "bottom-right" as const,
                          visibility: "hover" as const,
                        },
                      ]
                    : []),
                ],
              },
            }}
            footer={{
              cta_label: ad.creative.call_to_action_titles?.[0] ?? "",
              landing_page: ad.creative.landing_pages?.[0] ?? "",
              title: ad.creative.titles?.[0] ?? "",
            }}
          />
        )}
      />

      {/* Edit Logo Dialog */}
      {selectedAdForLogoEdit && (
        <EditLogoDialog
          open={editLogoDialogOpen}
          onOpenChange={(open) => {
            setEditLogoDialogOpen(open);
            setSelectedAdForLogoEdit(null);
            if (!open) {
              setOpenGallery(true);
            }
          }}
          creative={selectedAdForLogoEdit}
          onSave={(image: ImageType) => {
            if (selectedPublishAdCreative) {
              // const newAdCreatives = selectedPublishAdCreative.ad_creatives.map((ad) =>
              //   ad.id === creative.id ? creative : ad,
              // );
              updatePublishAdCreative({
                ...selectedPublishAdCreative,
                ad_creative: {
                  ...selectedPublishAdCreative.ad_creative,
                  creative: {
                    ...selectedPublishAdCreative.ad_creative.creative,
                    images: selectedPublishAdCreative.ad_creative.creative.images.map(
                      (img) => (img.id === image.id ? image : img),
                    ),
                  },
                },
              });
              setSelectedPublishAdCreative({
                ...selectedPublishAdCreative,
                ad_creative: {
                  ...selectedPublishAdCreative.ad_creative,
                  creative: {
                    ...selectedPublishAdCreative.ad_creative.creative,
                    images: selectedPublishAdCreative.ad_creative.creative.images.map(
                      (img) => (img.id === image.id ? image : img),
                    ),
                  },
                },
              });
              const newGalleryAdCreatives = galleryAdCreatives.map((ad) =>
                ad.creative.images[0].id === image.id
                  ? {
                      ...ad,
                      creative: {
                        ...ad.creative,
                        images: [image],
                        configuration: image.metadata?.configuration,
                      },
                    }
                  : ad,
              );
              setGalleryAdCreatives(newGalleryAdCreatives);
            }
          }}
        />
      )}
    </>
  );
};

export default UnpublishedTable;
